// Six visible digit fields. The Claude registration kit draws these as real
// inputs; a clipped 1px field behind inert boxes is what made iPad refuse to
// type or paste a code.

import { useRef, type ReactNode } from "react";

export function CodeBoxes({
  id,
  value,
  disabled,
  onValue,
  onComplete,
}: {
  readonly id: string;
  readonly value: string;
  readonly disabled?: boolean;
  readonly onValue: (next: string) => void;
  readonly onComplete?: (code: string) => void;
}): ReactNode {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = value.replace(/\D/g, "").slice(0, 6);
  const at = (i: number) => digits[i] ?? "";

  const focus = (i: number) => {
    const el = refs.current[i];
    if (el === undefined || el === null) return;
    el.focus();
    el.select();
  };

  const put = (next: string) => {
    const code = next.replace(/\D/g, "").slice(0, 6);
    onValue(code);
    if (code.length === 6 && onComplete !== undefined) onComplete(code);
  };

  return (
    <div className="web-code" role="group" aria-label="Six-digit code">
      {Array.from({ length: 6 }, (_, i) => (
        <input
          key={i}
          id={i === 0 ? id : undefined}
          className="web-code-box"
          name={i === 0 ? "code" : undefined}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          aria-label={`Digit ${i + 1} of 6`}
          disabled={disabled === true}
          value={at(i)}
          ref={(el) => {
            refs.current[i] = el;
          }}
          onChange={(event) => {
            const got = event.target.value.replace(/\D/g, "");
            if (got === "") {
              const next = (digits.slice(0, i) + digits.slice(i + 1)).slice(0, 6);
              onValue(next);
              return;
            }
            if (got.length > 1) {
              const next = (digits.slice(0, i) + got).replace(/\D/g, "").slice(0, 6);
              put(next);
              focus(Math.min(5, i + got.length));
              return;
            }
            const next = (digits.slice(0, i) + got + digits.slice(i + 1)).slice(0, 6);
            put(next);
            if (i < 5) focus(i + 1);
          }}
          onKeyDown={(event) => {
            if (event.key === "Backspace" && at(i) === "" && i > 0) {
              event.preventDefault();
              onValue(digits.slice(0, i - 1));
              focus(i - 1);
              return;
            }
            if (event.key === "ArrowLeft" && i > 0) {
              event.preventDefault();
              focus(i - 1);
            }
            if (event.key === "ArrowRight" && i < 5) {
              event.preventDefault();
              focus(i + 1);
            }
          }}
          onPaste={(event) => {
            const got = (event.clipboardData.getData("text") || "").replace(/\D/g, "");
            if (got === "") return;
            event.preventDefault();
            const next = (digits.slice(0, i) + got).replace(/\D/g, "").slice(0, 6);
            put(next);
            focus(Math.min(5, Math.max(0, next.length - 1)));
          }}
          onFocus={(event) => event.target.select()}
        />
      ))}
    </div>
  );
}
